import React from 'react';

import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { getPosHandlerNode } from '@atlaskit/editor-common/types';
import type { Node as PmNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView, NodeView } from '@atlaskit/editor-prosemirror/view';

import { pluginKey } from '../../common/agentManagedExtensionPluginType';
import { AgentManagedExtensionView } from '../../common/AgentManagedExtensionView';
import {
	getAgentManagedBlockLifecycleState,
	hasGeneratedBodyContent,
} from '../../common/lifecycle';
import { getNodeAiManagedObject, isAgentManagedExtension } from '../../common/utils';
import { executeAiManagedObject } from '../ui/executeAiManagedObject';

import { attachBodyClickSelection, createNodeViewDom } from './nodeview-dom';

interface NodeViewArgs {
	nodeViewPortalProviderAPI: PortalProviderAPI;
}

const buildPortalKey = (node: PmNode): string =>
	`agent-managed-extension-${(node.attrs.localId as string) ?? Math.random().toString(36)}`;

/**
 * Registry of active agent-managed-extension node views.
 * Used by the plugin's `view.update()` to trigger re-renders when plugin
 * state changes (e.g. config open/close), since ProseMirror only calls
 * `NodeView.update()` when the underlying node itself changes.
 */
const activeNodeViews = new Set<AgentManagedExtensionNodeView>();

/**
 * Re-renders all active agent-managed-extension node views.
 *
 * Keep new callers scoped with `rerenderNodeViewsForEditorView()` or
 * `setNodeViewSelectedByLocalId()` where possible.
 */
export const rerenderAllNodeViews = (): void => {
	for (const nodeView of activeNodeViews) {
		nodeView.renderReact();
	}
};

export const rerenderNodeViewsForEditorView = (view: EditorView): void => {
	for (const nodeView of activeNodeViews) {
		if (nodeView.belongsToEditorView(view)) {
			nodeView.renderReact();
		}
	}
};

/**
 * Marks a specific node view as selected/deselected and re-renders just that
 * node view. Returns true when a matching node view was found.
 */
export const setNodeViewSelectedByLocalId = (localId: string, selected: boolean): boolean => {
	for (const nv of activeNodeViews) {
		if (nv.getLocalId() === localId) {
			nv.setSelected(selected);
			nv.renderReact();
			return true;
		}
	}
	return false;
};

/**
 * Custom nodeView for bespoke agent-managed chrome. It exposes `contentDOM`,
 * matching regular bodied extensions so the body ADF is editable whenever the
 * host editor itself is editable.
 */
class AgentManagedExtensionNodeView implements NodeView {
	public dom: HTMLDivElement;
	public contentDOM?: HTMLDivElement;

	private node: PmNode;
	private view: EditorView;
	private nodeViewPortalProviderAPI: PortalProviderAPI;
	private mountPoint: HTMLDivElement;
	private portalKey: string;
	private detachBodyClick: () => void;
	private selected = false;

	constructor(node: PmNode, view: EditorView, getPos: getPosHandlerNode, args: NodeViewArgs) {
		this.node = node;
		this.view = view;
		this.nodeViewPortalProviderAPI = args.nodeViewPortalProviderAPI;

		const { root, mountPoint, contentDOM } = createNodeViewDom(view.dom.ownerDocument, node, {
			includeContentDOM: true,
		});
		this.dom = root;
		this.mountPoint = mountPoint;
		this.contentDOM = contentDOM;
		this.portalKey = buildPortalKey(node);

		this.detachBodyClick = attachBodyClickSelection(view, this.dom, getPos);
		activeNodeViews.add(this);
		this.renderReact();
	}

	getLocalId(): string {
		return (this.node.attrs.localId as string) ?? '';
	}

	belongsToEditorView(view: EditorView): boolean {
		return this.view === view;
	}

	setSelected(selected: boolean) {
		this.selected = selected;
	}

	renderReact = () => {
		const localId = this.getLocalId();
		const pluginState = pluginKey.getState(this.view.state);
		const aimoId = getNodeAiManagedObject(this.node.attrs)?.id;
		const transientLifecycleState = localId
			? pluginState?.executionLifecycleByLocalId?.[localId]?.state
			: undefined;
		const lifecycleState = getAgentManagedBlockLifecycleState({
			hasLocalExecutionError: Boolean(localId && pluginState?.executionErrorLocalIds?.[localId]),
			node: this.node,
			transientLifecycleState,
		});
		const element = (
			<AgentManagedExtensionView
				contentDOM={this.contentDOM}
				hasGeneratedBodyContent={hasGeneratedBodyContent(this.node)}
				isSelected={this.selected}
				lifecycleState={lifecycleState}
				onRetry={
					this.view.editable && aimoId
						? () => {
								void executeAiManagedObject({
									aimoId,
									editorView: this.view,
									localId: localId || undefined,
								});
							}
						: undefined
				}
				readOnly={!this.view.editable}
			/>
		);
		this.nodeViewPortalProviderAPI.render(() => element, this.mountPoint, this.portalKey);
	};

	update(node: PmNode): boolean {
		if (node.type !== this.node.type) {
			return false;
		}
		// Refuse to re-use this nodeView for a non-agent-managed block;
		// ProseMirror will then ask the factory for a fresh nodeView.
		if (!isAgentManagedExtension(node)) {
			return false;
		}
		// Force a remount when the localId changes — prevents stale
		// React state from leaking across instances.
		if (node.attrs.localId !== this.node.attrs.localId) {
			return false;
		}
		if (node === this.node) {
			return true;
		}
		this.dom.setAttribute('data-local-id', node.attrs.localId ?? '');
		this.node = node;
		this.renderReact();
		return true;
	}

	selectNode() {
		this.selected = true;
		this.renderReact();
	}

	deselectNode() {
		this.selected = false;
		this.renderReact();
	}

	destroy() {
		activeNodeViews.delete(this);
		this.detachBodyClick();
		this.nodeViewPortalProviderAPI.remove(this.portalKey);
	}

	stopEvent(event: Event): boolean {
		// Let ProseMirror own selected-node copy/cut so it serializes the extension ADF
		// instead of browser DOM, which includes visual chrome like the block label.
		if (event.type === 'copy' || event.type === 'cut') {
			return false;
		}

		if (this.contentDOM?.contains(event.target as Node)) {
			return false;
		}
		return true;
	}

	ignoreMutation(mutation: MutationRecord | { target: Node; type: 'selection' }): boolean {
		if (mutation.type === 'selection') {
			return false;
		}

		return !this.contentDOM?.contains(mutation.target);
	}
}

export const getAgentManagedExtensionNodeView =
	(args: NodeViewArgs) =>
	(node: PmNode, view: EditorView, getPos: getPosHandlerNode): NodeView => {
		// Only handle agent-managed bodied extensions; let the framework's
		// default `bodiedExtension` nodeView render every other one.
		if (!isAgentManagedExtension(node)) {
			return null as unknown as NodeView;
		}
		return new AgentManagedExtensionNodeView(node, view, getPos, args);
	};
